import React, { useState } from 'react';
import { ChannelFormData } from '../types/Channel';
import { getFaviconUrl } from '../utils/channelStorage';
import './AddChannelCard.css';

interface AddChannelCardProps {
  isEditMode: boolean;
  onAdd: (channelData: ChannelFormData) => void;
}

const AddChannelCard: React.FC<AddChannelCardProps> = ({ isEditMode, onAdd }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<ChannelFormData>({
    displayName: '',
    link: '',
    iconUrl: ''
  });

  const handleInputChange = (field: keyof ChannelFormData, value: string) => {
    // Auto-generate favicon when link changes
    if (field === 'link') {
      const newIconUrl = getFaviconUrl(value);
      setFormData(prev => ({ ...prev, [field]: value, iconUrl: newIconUrl }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.displayName.trim() && formData.link.trim()) {
      // Ensure we have an icon URL, generate one if needed
      const finalFormData = {
        ...formData,
        iconUrl: formData.iconUrl || getFaviconUrl(formData.link)
      };
      onAdd(finalFormData);
      setFormData({
        displayName: '',
        link: '',
        iconUrl: ''
      });
      setIsAdding(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      displayName: '',
      link: '',
      iconUrl: ''
    });
    setIsAdding(false);
  };

  if (!isEditMode) {
    return null;
  }

  if (!isAdding) {
    return (
      <div className="add-channel-card" onClick={() => setIsAdding(true)}>
        <div className="add-icon">+</div>
        <div className="add-text">Add Channel</div>
      </div>
    );
  }

  return (
    <div className="add-channel-card adding">
      <div className="add-channel-form">
        <h3>Add New Channel</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="channel-name">Display Name:</label>
            <input
              id="channel-name"
              type="text"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              placeholder="e.g., Netflix"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="channel-link">Link:</label>
            <input
              id="channel-link"
              type="url"
              value={formData.link}
              onChange={(e) => handleInputChange('link', e.target.value)}
              placeholder="https://netflix.com"
              required
            />
          </div>

          {formData.link && (
            <div className="icon-preview">
              <span>Icon Preview:</span>
              <img 
                src={formData.iconUrl} 
                alt="Channel icon"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          )}
          
          <div className="form-actions">
            <button type="submit" className="submit-btn">
              Add Channel
            </button>
            <button type="button" className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddChannelCard;
